import React from "react";
import "./research.css";
import Icon from "../ui/Icon";
import Reveal from "../ui/Reveal";
import BrandIcon from "../ui/BrandIcon";
import ProjectArt from "../visuals/ProjectArt";
import {
  profile,
  publications,
  researchInProgress,
  researchInterests,
} from "../../data/site";
import { projects, statusTone } from "../work/Data";

const bySlug = Object.fromEntries(projects.map((p) => [p.slug, p]));

const Authors = ({ authors, me }) => (
  <p className="paper__authors">
    {authors.map((name, i) => (
      <React.Fragment key={name}>
        {i > 0 && ", "}
        {name === me ? <strong>{name}</strong> : name}
      </React.Fragment>
    ))}
  </p>
);

/** Research, kept compact: the paper, the studies in progress, and where to find the rest. */
const Research = ({ onOpenProject }) => (
  <section
    className="research section"
    id="research"
    aria-labelledby="research-title"
  >
    <div className="container">
      <Reveal as="header" className="section__head">
        <span className="eyebrow">02 — Research</span>
        <h2 id="research-title" className="section__title">
          Research
        </h2>
        <p className="section__lead">
          One published paper and two studies in progress.
        </p>
      </Reveal>

      {publications.map((paper) => {
        const project = bySlug[paper.project];
        return (
          <Reveal
            as="article"
            key={paper.id}
            className="paper"
            aria-labelledby={`paper-${paper.id}`}
          >
            <div className="paper__body">
              <p className="paper__badge">
                <span className="paper__dot" aria-hidden="true" />
                Published · {paper.publisher}
              </p>
              <h3 id={`paper-${paper.id}`} className="paper__title">
                {paper.title}
              </h3>
              <Authors authors={paper.authors} me={paper.me} />
              <p className="paper__venue">
                {paper.venue}
                <span> · {paper.date}</span>
              </p>
              {paper.presented && <p className="paper__presented">{paper.presented}</p>}
              <div className="paper__actions">
                <a
                  className="btn btn--primary"
                  href={paper.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  Read on IEEE Xplore <Icon name="external" />
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
                {project && (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => onOpenProject(project.slug)}
                  >
                    The code behind it <Icon name="arrow" />
                  </button>
                )}
              </div>
            </div>

            <div className="frame paper__figure" aria-hidden="true">
              <div className="frame__bar">
                <span>THE PAPER&apos;S IDEA</span>
                <span>cudanet</span>
              </div>
              <div className="frame__body">
                <ProjectArt slug="cudanet-paper" />
                <ProjectArt slug="cudanet-paper-narrow" />
              </div>
            </div>
          </Reveal>
        );
      })}

      <ul className="study__list">
        {researchInProgress
          .map((slug) => bySlug[slug])
          .filter(Boolean)
          .map((p, i) => (
            <li key={p.slug} className="study__item">
              <Reveal as="article" index={i} fill className="study">
                <p
                  className={`study__status study__status--${statusTone[p.status]}`}
                >
                  {p.status}
                </p>
                <h4 className="study__title">{p.title}</h4>
                <p className="study__tagline">{p.tagline}</p>
                <button
                  type="button"
                  className="study__open"
                  onClick={() => onOpenProject(p.slug)}
                >
                  Case file <Icon name="arrow" />
                </button>
              </Reveal>
            </li>
          ))}
      </ul>

      <Reveal as="div" className="research__foot">
        <ul className="research__chips" aria-label="Research interests">
          {researchInterests.map((topic) => (
            <li key={topic}>{topic}</li>
          ))}
        </ul>
        <div className="research__links">
          <a
            className="btn"
            href={profile.scholar}
            target="_blank"
            rel="noreferrer"
          >
            <BrandIcon name="googlescholar" /> Google Scholar
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
          <a
            className="btn"
            href={profile.orcid}
            target="_blank"
            rel="noreferrer"
          >
            <BrandIcon name="orcid" /> ORCID
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export default Research;
